import { BaseModule } from './baseModule';
import type { AxiosInstance } from 'axios';
import type { ErrorHandler } from './errorHandler';
import type {
  GenerateJobDescriptionRequest,
  GenerateJobBenefitsRequest,
  GenerateJobRequirementsRequest,
  SuggestSkillsRequest,
  GenerateCandidateEmailRequest,
  GenerateSocialPostRequest,
  AIGenerationResponse,
  SocialPostResponse,
  RequestConfig,
  GenerateFromTemplateRequest,
  GenerateFromTemplateResponse,
} from './types';

/**
 * AI Module - Handles all AI-powered content generation API operations
 * 
 * This module provides AI generation operations for job content, candidate emails,
 * and social media posts using OpenAI integration.
 */
export class AIModule extends BaseModule {
  constructor(httpClient: AxiosInstance, errorHandler: ErrorHandler) {
    super(httpClient, errorHandler);
  }

  // ============================================================================
  // Job Content Generation
  // ============================================================================

  /**
   * Generate job description using AI
   * POST /api/admin/jobs/ai/generate-description
   */
  async generateJobDescription(
    data: GenerateJobDescriptionRequest,
    config?: RequestConfig
  ): Promise<AIGenerationResponse> {
    try {
      return await this.post<AIGenerationResponse>(
        '/api/admin/jobs/ai/generate-description',
        data,
        { timeout: 180000, ...config } // 3 minutes timeout for AI generation
      );
    } catch (error) {
      // Handle AI service unavailable error gracefully
      const apiError = this.errorHandler.handleError(error);
      if (apiError.status === 503) {
        throw new Error('AI service is currently unavailable. Please check your OpenAI API configuration.');
      }
      throw apiError;
    }
  }

  /**
   * Generate job benefits using AI
   * POST /api/admin/jobs/ai/generate-benefits
   */
  async generateJobBenefits(
    data: GenerateJobBenefitsRequest,
    config?: RequestConfig
  ): Promise<AIGenerationResponse> {
    try {
      return await this.post<AIGenerationResponse>(
        '/api/admin/jobs/ai/generate-benefits',
        data,
        config
      );
    } catch (error) {
      const apiError = this.errorHandler.handleError(error);
      if (apiError.status === 503) {
        throw new Error('AI service is currently unavailable. Please check your OpenAI API configuration.');
      }
      throw apiError;
    }
  }

  /**
   * Generate job requirements using AI
   * POST /api/admin/jobs/ai/generate-requirements
   */
  async generateJobRequirements(
    data: GenerateJobRequirementsRequest,
    config?: RequestConfig
  ): Promise<AIGenerationResponse> {
    try {
      return await this.post<AIGenerationResponse>(
        '/api/admin/jobs/ai/generate-requirements',
        data,
        config
      );
    } catch (error) {
      const apiError = this.errorHandler.handleError(error);
      if (apiError.status === 503) {
        throw new Error('AI service is currently unavailable. Please check your OpenAI API configuration.');
      }
      throw apiError;
    }
  }

  /**
   * Suggest skills for a job using AI
   * POST /api/admin/jobs/ai/suggest-skills
   */
  async suggestSkills(
    data: SuggestSkillsRequest,
    config?: RequestConfig
  ): Promise<AIGenerationResponse> {
    try {
      return await this.post<AIGenerationResponse>(
        '/api/admin/jobs/ai/suggest-skills',
        data,
        config
      );
    } catch (error) {
      const apiError = this.errorHandler.handleError(error);
      if (apiError.status === 503) {
        throw new Error('AI service is currently unavailable. Please check your OpenAI API configuration.');
      }
      throw apiError;
    }
  }

  // ============================================================================
  // Job Description Analysis
  // ============================================================================

  /**
   * Analyze job description for bias
   * POST /api/admin/jobs/ai/analyze-bias
   */
  async analyzeJobDescriptionBias(
    description: string,
    config?: RequestConfig
  ): Promise<AIGenerationResponse> {
    try {
      return await this.post<AIGenerationResponse>(
        '/api/admin/jobs/ai/analyze-bias',
        { description },
        config
      );
    } catch (error) {
      const apiError = this.errorHandler.handleError(error);
      if (apiError.status === 503) {
        throw new Error('AI service is currently unavailable. Please check your OpenAI API configuration.');
      }
      throw apiError;
    }
  }

  /**
   * Calculate readability score for job description
   * POST /api/admin/jobs/ai/readability-score
   */
  async calculateReadabilityScore(
    text: string,
    config?: RequestConfig
  ): Promise<{ score: number; level: string; suggestions: string[] }> {
    try {
      return await this.post<{ score: number; level: string; suggestions: string[] }>(
        '/api/admin/jobs/ai/readability-score',
        { text },
        config
      );
    } catch (error) {
      const apiError = this.errorHandler.handleError(error);
      if (apiError.status === 503) {
        throw new Error('AI service is currently unavailable. Please check your OpenAI API configuration.');
      }
      throw apiError;
    }
  }

  // ============================================================================
  // Candidate Communication
  // ============================================================================

  /**
   * Generate candidate email using AI
   * POST /api/admin/candidates/ai/generate-email
   */
  async generateCandidateEmail(
    data: GenerateCandidateEmailRequest,
    config?: RequestConfig
  ): Promise<AIGenerationResponse> {
    try {
      return await this.post<AIGenerationResponse>(
        '/api/admin/candidates/ai/generate-email',
        data,
        config
      );
    } catch (error) {
      const apiError = this.errorHandler.handleError(error);
      if (apiError.status === 503) {
        throw new Error('AI service is currently unavailable. Please check your OpenAI API configuration.');
      }
      throw apiError;
    }
  }

  // ============================================================================
  // Social Media Content Generation
  // ============================================================================

  /**
   * Generate social media post image
   * POST /api/admin/jobs/ai/generate-social-post
   */
  async generateSocialPost(
    data: GenerateSocialPostRequest,
    config?: RequestConfig
  ): Promise<SocialPostResponse> {
    try {
      return await this.post<SocialPostResponse>(
        '/api/admin/jobs/ai/generate-social-post',
        data,
        { timeout: 180000, ...config } // 3 minutes timeout for image generation
      );
    } catch (error) {
      const apiError = this.errorHandler.handleError(error);
      if (apiError.status === 503) {
        throw new Error('AI service is currently unavailable. Please check your OpenAI API configuration.');
      }
      throw apiError;
    }
  }

  // ============================================================================
  // Generate All Job Content (Single API Call)
  // ============================================================================

  /**
   * Generate all job content fields in a single API call
   * POST /api/admin/jobs/ai/generate-all
   */
  async generateAllJobContent(
    data: {
      job_title: string;
      experience_level?: string;
      company_id?: string;
      company_name?: string;
      company_industry?: string;
      company_description?: string;
      location?: string;
    },
    config?: RequestConfig
  ): Promise<{
    description?: string;
    requirements?: string[];
    benefits?: string[];
    skills?: string[];
    errors: string[];
  }> {
    try {
      return await this.post<{
        description?: string;
        requirements?: string[];
        benefits?: string[];
        skills?: string[];
        errors: string[];
      }>(
        '/api/admin/jobs/ai/generate-all',
        data,
        { timeout: 300000, ...config } // 5 minutes timeout for all generations
      );
    } catch (error) {
      const apiError = this.errorHandler.handleError(error);
      if (apiError.status === 503) {
        throw new Error('AI service is currently unavailable. Please check your OpenAI API configuration.');
      }
      throw apiError;
    }
  }

  // ============================================================================
  // AI Template Generation (Requirements 2.2, 4.4)
  // ============================================================================

  /**
   * Generate job content from an AI template
   * POST /api/admin/jobs/ai/generate-from-template
   * 
   * This method sends the template context combined with company information
   * to the AI service and returns generated job content.
   * 
   * @param templateId - The AI template ID to generate from
   * @param companyId - The company ID for context
   * @param jobTitle - Optional job title override
   * @param config - Optional request configuration
   * @returns Generated job content fields
   */
  async generateFromTemplate(
    templateId: string,
    companyId: string,
    jobTitle?: string,
    config?: RequestConfig
  ): Promise<GenerateFromTemplateResponse> {
    const requestData: GenerateFromTemplateRequest = {
      template_id: templateId,
      company_id: companyId,
      job_title: jobTitle,
    };

    try {
      return await this.post<GenerateFromTemplateResponse>(
        '/api/admin/jobs/ai/generate-from-template',
        requestData,
        { timeout: 180000, ...config } // 3 minutes timeout for AI generation
      );
    } catch (error) {
      const apiError = this.errorHandler.handleError(error);
      
      // Handle specific error cases
      if (apiError.status === 503) {
        throw new Error('AI service is currently unavailable. Please try again later.');
      }
      if (apiError.status === 504) {
        throw new Error('AI generation timed out. Please try again.');
      }
      if (apiError.status === 404) {
        throw new Error('Template or company not found.');
      }
      if (apiError.status === 400) {
        throw new Error('Invalid template context format.');
      }
      
      throw apiError;
    }
  }

  /**
   * Generate job content from an AI template with progress callback
   * This is a convenience wrapper that provides progress updates
   * 
   * @param templateId - The AI template ID to generate from
   * @param companyId - The company ID for context
   * @param onProgress - Callback for progress updates
   * @param jobTitle - Optional job title override
   * @param config - Optional request configuration
   * @returns Generated job content fields
   */
  async generateFromTemplateWithProgress(
    templateId: string,
    companyId: string,
    onProgress: (status: 'starting' | 'generating' | 'complete' | 'error', message?: string) => void,
    jobTitle?: string,
    config?: RequestConfig
  ): Promise<GenerateFromTemplateResponse> {
    try {
      onProgress('starting', 'Preparing AI generation...');
      
      // Small delay to show the starting state
      await new Promise(resolve => setTimeout(resolve, 100));
      
      onProgress('generating', 'Generating content from template...');
      
      const result = await this.generateFromTemplate(templateId, companyId, jobTitle, config);
      
      onProgress('complete', 'Content generated successfully');
      
      return result;
    } catch (error) {
      onProgress('error', error instanceof Error ? error.message : 'Generation failed');
      throw error;
    }
  }

  /**
   * Validate generate from template request
   */
  validateGenerateFromTemplateRequest(data: GenerateFromTemplateRequest): {
    valid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];

    if (!data.template_id || data.template_id.trim().length === 0) {
      errors.push('Template ID is required');
    }

    if (!data.company_id || data.company_id.trim().length === 0) {
      errors.push('Company ID is required');
    }

    return {
      valid: errors.length === 0,
      errors,
    };
  }

  // ============================================================================
  // Convenience Methods
  // ============================================================================

  /**
   * Generate complete job content (description, benefits, requirements, skills)
   * @deprecated Use generateAllJobContent for better performance
   */
  async generateCompleteJobContent(
    jobTitle: string,
    experienceLevel?: string,
    companyName?: string,
    config?: RequestConfig
  ): Promise<{
    description: string;
    benefits: string[];
    requirements: { required: string[]; preferred: string[] };
    skills: string[];
  }> {
    try {
      // Execute all generations in parallel for better performance
      const [descriptionRes, benefitsRes, requirementsRes, skillsRes] = await Promise.all([
        this.generateJobDescription({ job_title: jobTitle, experience_level: experienceLevel }, config),
        this.generateJobBenefits({ job_title: jobTitle, level: experienceLevel, company_name: companyName }, config),
        this.generateJobRequirements({ job_title: jobTitle, seniority_level: experienceLevel }, config),
        this.suggestSkills({ job_title: jobTitle }, config),
      ]);

      return {
        description: typeof descriptionRes.content === 'string' ? descriptionRes.content : '',
        benefits: Array.isArray(benefitsRes.content) ? benefitsRes.content : [],
        requirements: typeof requirementsRes.content === 'object' && 
                      requirementsRes.content !== null &&
                      !Array.isArray(requirementsRes.content)
          ? requirementsRes.content as { required: string[]; preferred: string[] }
          : { required: [], preferred: [] },
        skills: Array.isArray(skillsRes.content) ? skillsRes.content : [],
      };
    } catch (error) {
      throw this.errorHandler.handleError(error);
    }
  }

  /**
   * Generate job description with bias analysis
   */
  async generateJobDescriptionWithAnalysis(
    data: GenerateJobDescriptionRequest,
    config?: RequestConfig
  ): Promise<{
    description: string;
    biasAnalysis: AIGenerationResponse;
    readabilityScore: { score: number; level: string; suggestions: string[] };
  }> {
    try {
      // First generate the description
      const descriptionRes = await this.generateJobDescription(data, config);
      const description = typeof descriptionRes.content === 'string' ? descriptionRes.content : '';

      // Then analyze it for bias and readability
      const [biasAnalysis, readabilityScore] = await Promise.all([
        this.analyzeJobDescriptionBias(description, config),
        this.calculateReadabilityScore(description, config),
      ]);

      return {
        description,
        biasAnalysis,
        readabilityScore,
      };
    } catch (error) {
      throw this.errorHandler.handleError(error);
    }
  }

  /**
   * Generate stage-specific candidate email
   */
  async generateStageEmail(
    candidateName: string,
    jobTitle: string,
    companyName: string,
    stage: 'interview' | 'offer' | 'rejection' | 'welcome',
    additionalContext?: string,
    config?: RequestConfig
  ): Promise<string> {
    try {
      const response = await this.generateCandidateEmail(
        {
          candidate_name: candidateName,
          job_title: jobTitle,
          company_name: companyName,
          stage: stage as any, // Type assertion for CandidateStage
          additional_context: additionalContext,
        },
        config
      );

      return typeof response.content === 'string' ? response.content : '';
    } catch (error) {
      throw this.errorHandler.handleError(error);
    }
  }

  /**
   * Generate social posts for multiple platforms
   */
  async generateMultiPlatformSocialPosts(
    jobId: string,
    platforms: Array<'instagram_square' | 'instagram_story' | 'linkedin_post' | 'twitter_post' | 'facebook_post'>,
    style?: 'professional' | 'modern' | 'creative' | 'minimalist',
    config?: RequestConfig
  ): Promise<Record<string, SocialPostResponse>> {
    try {
      const results = await Promise.all(
        platforms.map(platform =>
          this.generateSocialPost({ job_id: jobId, platform, style }, config)
            .then(response => ({ platform, response }))
        )
      );

      return results.reduce((acc, { platform, response }) => {
        acc[platform] = response;
        return acc;
      }, {} as Record<string, SocialPostResponse>);
    } catch (error) {
      throw this.errorHandler.handleError(error);
    }
  }

  /**
   * Check if AI service is available
   */
  async checkAIServiceAvailability(config?: RequestConfig): Promise<boolean> {
    try {
      // Try a simple AI operation to check availability
      await this.suggestSkills({ job_title: 'Test' }, { ...config, timeout: 5000 });
      return true;
    } catch (error) {
      const apiError = this.errorHandler.handleError(error);
      return apiError.status !== 503;
    }
  }

  /**
   * Validate AI generation request data
   */
  validateGenerationRequest(data: any): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!data) {
      errors.push('Request data is required');
      return { valid: false, errors };
    }

    // Validate job title for job-related generations
    if ('job_title' in data) {
      if (!data.job_title || data.job_title.trim().length === 0) {
        errors.push('Job title is required');
      }
      if (data.job_title && data.job_title.length > 200) {
        errors.push('Job title cannot exceed 200 characters');
      }
    }

    // Validate candidate name for email generation
    if ('candidate_name' in data) {
      if (!data.candidate_name || data.candidate_name.trim().length === 0) {
        errors.push('Candidate name is required');
      }
    }

    // Validate job ID for social post generation
    if ('job_id' in data) {
      if (!data.job_id || data.job_id.trim().length === 0) {
        errors.push('Job ID is required');
      }
    }

    return {
      valid: errors.length === 0,
      errors,
    };
  }
}
